import { Component, Input, Output, EventEmitter, inject, ChangeDetectionStrategy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  SchoolRosterStudent,
  UpdateSchoolRosterStudentRequest,
  ModalType,
  ToastMessageType,
  RosterProgressStatuses
} from '@k12/models';
import { SchoolsService } from '@k12/school-services';
import { ToastMessageService } from '@k12/app-services';
import { ModalDialogComponent } from '@k12/ui-primitives';
import { rosterActionErrorMessage } from '../roster-action-error';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-admin-actions',
  templateUrl: './admin-actions.component.html',
  styleUrl: './admin-actions.component.scss',
  standalone: true,
  imports: [MatButtonModule, MatProgressSpinnerModule, MatTooltipModule]
})
export class AdminActionsComponent {
  @Input() student: SchoolRosterStudent | null = null;
  @Input() form!: FormGroup;
  @Input() isNotAttending = false;
  @Input() disableRevert = false;
  @Input() disableRevertMessage = 'Operation not possible for this school type.';
  @Input() individualCostErrors: string[] = [];
  @Output() saved = new EventEmitter<string>();
  @Output() reverted = new EventEmitter<string>();
  @Output() validateForm = new EventEmitter<void>();
  @Output() syncFormToModel = new EventEmitter<void>();

  private readonly schoolsService = inject(SchoolsService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly dialog = inject(MatDialog);

  isSaving = false;
  isReverting = false;

  get isSaveDisabled(): boolean {
    if (this.isNotAttending) return this.isSaving;
    return this.form.invalid || this.individualCostErrors.length > 0 || this.isSaving;
  }

  get canRevert(): boolean {
    if (!this.student) return false;
    const status = this.student.rosterProgressStatusId;
    // Can revert if Certified (2) or Endorsed (3)
    return status === RosterProgressStatuses.Certified || status === RosterProgressStatuses.Endorsed;
  }

  get isRevertDisabledByPolicy(): boolean {
    return this.disableRevert && this.canRevert;
  }

  get revertLabel(): string {
    if (!this.student) return '';
    const status = this.student.rosterProgressStatusId;
    if (status === RosterProgressStatuses.Endorsed) return 'Revert Endorsement';
    if (status === RosterProgressStatuses.Certified) return 'Revert Certification';
    return '';
  }

  onSave(): void {
    if (!this.canSave()) return;

    this.isSaving = true;
    this.syncFormToModel.emit();

    if (!this.student) return;

    const dto = this.createSaveDto();
    this.submitSave(dto);
  }

  private canSave(): boolean {
    if (this.isSaving) return false;

    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.validateForm.emit();

    if (!this.isNotAttending && (this.form.invalid || this.individualCostErrors.length > 0)) {
      this.toastMessageService.showToast(ToastMessageType.Error, 'Please fix validation errors before saving');
      return false;
    }

    if (!this.student) {
      this.toastMessageService.showToast(ToastMessageType.Error, 'No student data available');
      return false;
    }

    return true;
  }

  private createSaveDto(): UpdateSchoolRosterStudentRequest {
    if (!this.student) throw new Error('No student available');

    const now = new Date();
    const termPayments = (this.student.crossTermSummary || [])
      .filter((term) => term.studentEnrollmentId || new Date(term.endDate) >= now)
      .map((term) => ({
        studentEnrollmentId: term.studentEnrollmentId || null,
        schoolTermGradeId: term.schoolTermGradeId,
        termCost: term.payment
      }));

    return {
      studentEnrollmentId: this.student.studentEnrollmentId,
      studentId: this.student.studentId,
      schoolProgramPartnerId: this.student.schoolProgramPartnerId,
      schoolTermGradeId: this.student.schoolTermGradeId || '',
      attendanceTypeId: this.student.attendanceTypeId,
      rosterProgressStatusId: this.student.rosterProgressStatusId,
      academicYearStandardCostTotal: this.student.academicYearStandardCostTotal,
      academicYearIndividualCostTotal: this.student.academicYearIndividualCostTotal,
      termCost: this.getCurrentTermCost(),
      termCosts: termPayments,
      certificationDate: this.student.certificationDate || null,
      endorsedDate: this.student.endorsedDate || null,
      startDate: this.student.startDate
    };
  }

  private getCurrentTermCost(): number | null | undefined {
    if (!this.student) return undefined;
    return (this.student.crossTermSummary || []).find((t) => t.schoolTermGradeId === this.student!.schoolTermGradeId)
      ?.payment;
  }

  private submitSave(dto: UpdateSchoolRosterStudentRequest): void {
    this.schoolsService.updateSchoolRosterStudent(dto).subscribe({
      next: (response) => {
        this.isSaving = false;
        if (response?.success) {
          if (this.student?.studentEnrollmentId) {
            this.saved.emit(this.student.studentEnrollmentId);
          }
          this.toastMessageService.showToast(ToastMessageType.Success, 'Student record updated successfully');
        } else {
          this.toastMessageService.showToast(
            ToastMessageType.Error,
            response?.message || 'Failed to update student record'
          );
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isSaving = false;
        this.toastMessageService.showToast(
          ToastMessageType.Error,
          rosterActionErrorMessage(error, 'An error occurred while saving')
        );
      }
    });
  }

  onRevert(): void {
    if (!this.canRevert || this.isReverting || this.isRevertDisabledByPolicy) return;

    const confirmMessage = this.getRevertConfirmMessage();
    const dialogRef = this.dialog.open(ModalDialogComponent, {
      backdropClass: 'custom-dialog-backdrop',
      data: {
        closeButtonText: 'Cancel',
        confirmButtonText: 'Revert',
        modalType: ModalType.ContinueCancelModal,
        title: confirmMessage
      },
      disableClose: true,
      panelClass: 'custom-dialog'
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.executeRevert();
      }
    });
  }

  private getRevertConfirmMessage(): string {
    if (!this.student) return '';
    const status = this.student.rosterProgressStatusId;

    if (status === RosterProgressStatuses.Endorsed) {
      return 'Are you sure you want to revert this endorsement? The student will be moved back to the Not Yet Endorsed roster.';
    }
    if (status === RosterProgressStatuses.Certified) {
      return 'Are you sure you want to revert this certification? The student will be moved back to the Not Yet Certified roster.';
    }
    return '';
  }

  private executeRevert(): void {
    if (!this.student) return;

    this.isReverting = true;
    const dto = this.createRevertDto();
    this.submitRevert(dto);
  }

  private createRevertDto(): UpdateSchoolRosterStudentRequest {
    if (!this.student) throw new Error('No student available');

    const currentStatus = this.student.rosterProgressStatusId;

    let newStatus: number;
    let certificationDate: string | null | undefined = this.student.certificationDate;
    let endorsedDate: string | null | undefined = this.student.endorsedDate;

    if (currentStatus === RosterProgressStatuses.Endorsed) {
      // Revert from Endorsed to Certified
      newStatus = RosterProgressStatuses.Certified;
      endorsedDate = null;
    } else if (currentStatus === RosterProgressStatuses.Certified) {
      // Revert from Certified to New
      newStatus = RosterProgressStatuses.New;
      certificationDate = null;
    } else {
      throw new Error('Cannot revert from current status');
    }

    const now = new Date();
    const termPayments = (this.student.crossTermSummary || [])
      .filter((term) => term.studentEnrollmentId || new Date(term.endDate) >= now)
      .map((term) => ({
        studentEnrollmentId: term.studentEnrollmentId || null,
        schoolTermGradeId: term.schoolTermGradeId,
        termCost: term.payment
      }));

    const dto: UpdateSchoolRosterStudentRequest = {
      studentEnrollmentId: this.student.studentEnrollmentId,
      studentId: this.student.studentId,
      schoolProgramPartnerId: this.student.schoolProgramPartnerId,
      schoolTermGradeId: this.student.schoolTermGradeId || '',
      attendanceTypeId: this.student.attendanceTypeId,
      rosterProgressStatusId: newStatus,
      academicYearStandardCostTotal: this.student.academicYearStandardCostTotal,
      academicYearIndividualCostTotal: this.student.academicYearIndividualCostTotal,
      termCost: this.getCurrentTermCost(),
      termCosts: termPayments,
      certificationDate: certificationDate || null,
      endorsedDate: endorsedDate || null,
      startDate: this.student.startDate
    };

    return dto;
  }

  private submitRevert(dto: UpdateSchoolRosterStudentRequest): void {
    this.schoolsService.updateSchoolRosterStudent(dto).subscribe({
      next: (response) => {
        this.isReverting = false;
        if (response?.success) {
          if (this.student?.studentEnrollmentId) {
            this.reverted.emit(this.student.studentEnrollmentId);
          }
          const message =
            dto.rosterProgressStatusId === RosterProgressStatuses.Certified
              ? 'Endorsement reverted successfully'
              : 'Certification reverted successfully';
          this.toastMessageService.showToast(ToastMessageType.Success, message);
        } else {
          this.toastMessageService.showToast(ToastMessageType.Error, response?.message || 'Failed to revert');
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isReverting = false;
        this.toastMessageService.showToast(
          ToastMessageType.Error,
          rosterActionErrorMessage(error, 'An error occurred while reverting')
        );
      }
    });
  }
}
